import TrainingsService from "services/TrainingsService";
const { createAsyncThunk, createSlice } = require("@reduxjs/toolkit");

export const getTrainings = createAsyncThunk(
  "trainings/getAll",
  async ({ page = 1, all = false }, { rejectWithValue }) => {
    try {
      if (all) {
        // collect all pages into one array
        let allTrainings = [];
        let currentPage = 1;
        let totalPages = 1;
        do {
          const response = await TrainingsService.getTrainings({
            page: currentPage,
            limit: 1000,
          });
          const results = response.data?.results;
          const data = results?.formations || response.data?.data || [];
          const pages = results?.totalPages || response.data?.pages || 1;

          allTrainings = allTrainings.concat(data);
          totalPages = pages;
          currentPage++;
        } while (currentPage <= totalPages);
        return {
          trainings: allTrainings,
          totalPages: 1, // or leave undefined if you just want everything
          currentPage: 1,
          pageSize: allTrainings.length,
        };
      } else {
        // normal, paged fetch
        const response = await TrainingsService.getTrainings({ page });
        const results = response.data?.results;
        return {
          trainings: results?.formations || response.data?.data || [],
          totalPages: results?.totalPages || response.data?.pages || 1,
          currentPage: results?.currentPage || response.data?.page || 1,
          pageSize: results?.limit || response.data?.limit || 20,
        };
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Erreur");
    }
  }
);

export const getTrainingsForKids = createAsyncThunk(
  "trainingsKids/getAllForKids",
  async ({ page = 1, all = false }, { rejectWithValue }) => {
    try {
      if (all) {
        // collect all pages into one array
        let allTrainings = [];
        let currentPage = 1;
        let totalPages = 1;
        do {
          const response = await TrainingsService.getTrainingsKids({
            page: currentPage,
            limit: 1000,
          });
          const data = response.data?.data || response.data?.results?.formations || [];
          const pages = response.data?.totalPages || response.data?.pages || 1;

          allTrainings = allTrainings.concat(data);
          totalPages = pages;
          currentPage++;
        } while (currentPage <= totalPages);
        return {
          trainingsForKids: allTrainings,
          totalPages: 1,
          currentPage: 1,
          pageSize: allTrainings.length,
        };
      } else {
        // normal, paged fetch
        const response = await TrainingsService.getTrainingsKids({ page });
        console.log(response.data);
        return {
          trainingsForKids: response.data?.data || response.data?.results?.formations || [],
          totalPages: response.data?.totalPages || response.data?.pages || 1,
          currentPage: response.data?.page || response.data?.currentPage || 1,
          pageSize: response.data?.limit || response.data?.pageSize || 20,
        };
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Erreur");
    }
  }
);

export const getTrainingById = createAsyncThunk(
  "trainings/getTrainingById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await TrainingsService.getTraining(id);
      return response.data?.formation;
    } catch (error) {
      return rejectWithValue(error.response || "Erreur");
    }
  }
);

export const fetchTrainingLanguages = createAsyncThunk(
  "trainings/fetchLanguages",
  async (_, { rejectWithValue }) => {
    try {
      const response = await TrainingsService.getLanguages();
      return response.data?.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Erreur");
    }
  }
);

export const createTraining = createAsyncThunk(
  "trainings/createTraining",
  async (data, { rejectWithValue }) => {
    try {
      const response = await TrainingsService.createTraining(data);
      return response.data.training;
    } catch (error) {
      console.log(error.response.data || "Erreur");
      return rejectWithValue(error.response?.data?.message || "Erreur");
    }
  }
);

export const updateTrainingById = createAsyncThunk(
  "trainings/updateTrainingById",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await TrainingsService.updateTraining(id, data);
      return response.data.formation;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Erreur");
    }
  }
);

export const deleteTrainingById = createAsyncThunk(
  "trainings/deleteTrainingById",
  async (id, { rejectWithValue }) => {
    try {
      await TrainingsService.deleteTraining(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response || "Erreur");
    }
  }
);

export const addStudentToTraining = createAsyncThunk(
  "trainings/addStudentToTraining",
  async ({ trainingId, students }, { rejectWithValue }) => {
    try {
      const response = await TrainingsService.addStudentToTraining(
        trainingId,
        students // Now passing array of students with payment details
      );
      // Return array of student formations
      return response.data?.studentsFormation?.map((studentFormation) => ({
        studentFormationId: studentFormation._id,
        studentId: studentFormation.studentId,
        studentName: studentFormation.studentName,
        studentEmail: studentFormation.studentEmail,
        studentPhoneNumber: studentFormation.studentPhoneNumber,
        studentCardNumber: studentFormation.studentCardNumber,
        sex: studentFormation.sex,
        payment: studentFormation.payment, // Include payment details
      }));
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Erreur");
    }
  }
);

export const deleteStudentFromTraining = createAsyncThunk(
  "trainings/deleteStudentFromTraining",
  async ({ trainingId, studentFormationId }, { rejectWithValue }) => {
    try {
      await TrainingsService.deleteStudentFromTraining(
        trainingId,
        studentFormationId
      );
      return {
        trainingId,
        studentFormationId,
      };
    } catch (error) {
      return rejectWithValue(error.response || "Erreur");
    }
  }
);

export const getTrainingStudents = createAsyncThunk(
  "trainings/getTrainingStudents",
  async (id, { rejectWithValue }) => {
    try {
      const response = await TrainingsService.getTrainingStudents(id);
      return response.data.students;
    } catch (error) {
      return rejectWithValue(error.response || "Erreur");
    }
  }
);

// Récupérer les entités studentFormation d'une formation précise
export const getStudentFormationsForFormation = createAsyncThunk(
  "trainings/getStudentFormationsForFormation",
  async ({ trainingId, groupId }, { rejectWithValue }) => {
    try {
      const response = await TrainingsService.getStudentFormationsForFormation(
        trainingId,
        groupId
      );
      console.log(
        "getStudentFormationsForFormation response:",
        response.data.data
      );
      return response.data.data;
    } catch (error) {
      console.error("Error:", error);
      return rejectWithValue(error.response?.data?.message || "Erreur");
    }
  }
);

// Action pour approuver un instructeur
export const approveInstructor = createAsyncThunk(
  "trainings/approveInstructor",
  async ({ trainingId, instructorId }, { rejectWithValue }) => {
    try {
      const response = await TrainingsService.approveInstructor(
        trainingId,
        instructorId
      );
      const training = response.data.training;
      if (!training?.instructors?.length) {
        throw new Error("Aucun instructeur retourné.");
      }
      const approvedInstructor = training.instructors.find(
        (inst) => inst.instructorId?._id === instructorId
      );
      if (!approvedInstructor) {
        throw new Error("Instructeur approuvé introuvable dans la réponse.");
      }
      return {
        trainingId,
        instructor: approvedInstructor,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Erreur lors de l'approbation"
      );
    }
  }
);

// Action pour rejeter un instructeur
export const rejectInstructor = createAsyncThunk(
  "trainings/rejectInstructor",
  async ({ trainingId, instructorId, reason }, { rejectWithValue }) => {
    try {
      await TrainingsService.rejectInstructor(trainingId, instructorId, reason);
      return {
        trainingId,
        instructorId,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Erreur lors du rejet"
      );
    }
  }
);

export const assignInstructorToTraining = createAsyncThunk(
  "trainings/assignInstructorToTraining",
  async ({ trainingId, instructorId }, { rejectWithValue }) => {
    try {
      const response = await TrainingsService.assignInstructorToTraining(
        trainingId,
        instructorId
      );
      return response?.data?.training;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Erreur lors de l'assignation"
      );
    }
  }
);

export const getStudentsForTraining = createAsyncThunk(
  "trainings/getStudentsForTraining",
  async (trainingId, { rejectWithValue }) => {
    try {
      const response = await TrainingsService.getStudentsForTraining(
        trainingId
      );
      return response.data.students;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Erreur");
    }
  }
);

export const removeDiscountFromTraining = createAsyncThunk(
  "trainings/removeDiscount",
  async (studentFormationId, { rejectWithValue }) => {
    try {
      const response = await TrainingsService.removeDiscount(
        studentFormationId
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Erreur");
    }
  }
);

const initialState = {
  trainings: [],
  trainingsForKids: [],
  training: null,
  loading: false,
  error: null,
  totalPages: 1,
  currentPage: 1,
  pageSize: 20,
  studentsForTraining: [],
  availableStudentFormations: [], // Added to store available student formations
  languages: ["fr", "en", "ar"], // language suggestions for the training form
};

const trainingsSlice = createSlice({
  name: "trainings",
  initialState,
  reducers: {
    setTrainings: (state, { payload }) => {
      state.trainings = payload;
    },
    setTraining: (state, { payload }) => {
      state.training = payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
    },
  },
  extraReducers: {
    [getTrainingsForKids.pending]: (state) => {
      state.loading = true;
    },
    [getTrainingsForKids.fulfilled]: (state, { payload }) => {
      // FIX: Correctly assign payload.trainingsForKids
      state.trainingsForKids = payload.trainingsForKids;
      state.totalPages = payload.totalPages;
      state.currentPage = payload.currentPage;
      state.pageSize = payload.pageSize;
      state.loading = false;
    },
    [getTrainingsForKids.rejected]: (state, { payload }) => {
      state.error = payload;
      state.loading = false;
    },
    [getTrainings.pending]: (state) => {
      state.loading = true;
    },
    [getTrainings.fulfilled]: (state, { payload }) => {
      state.trainings = payload.trainings;
      state.totalPages = payload.totalPages;
      state.currentPage = payload.currentPage;
      state.pageSize = payload.pageSize;
      state.loading = false;
    },
    [getTrainings.rejected]: (state, { payload }) => {
      state.error = payload;
      state.totalPages = 1;
      state.currentPage = 1;
      state.pageSize = 20;
      state.loading = false;
    },
    [getTrainingById.pending]: (state) => {
      state.loading = true;
    },
    [getTrainingById.fulfilled]: (state, { payload }) => {
      state.training = payload;
      state.loading = false;
    },
    [getTrainingById.rejected]: (state, { payload }) => {
      state.loading = false;
    },
    [fetchTrainingLanguages.fulfilled]: (state, { payload }) => {
      if (Array.isArray(payload) && payload.length) {
        state.languages = payload;
      }
    },
    [createTraining.pending]: (state, { payload }) => {
      state.loading = true;
    },
    [createTraining.fulfilled]: (state, { payload }) => {
      state.trainings.push(payload);
      state.loading = false;
    },
    [createTraining.rejected]: (state, { payload }) => {
      state.loading = false;
    },
    [updateTrainingById.pending]: (state) => {
      state.loading = true;
    },
    [updateTrainingById.fulfilled]: (state, { payload }) => {
      state.trainings = state.trainings.map((training) =>
        training._id === payload._id ? payload : training
      );
      state.loading = false;
    },
    [updateTrainingById.rejected]: (state, { payload }) => {
      state.loading = false;
    },
    [deleteTrainingById.pending]: (state) => {
      state.loading = true;
    },
    [deleteTrainingById.fulfilled]: (state, { payload }) => {
      state.trainings = state.trainings.filter(
        (training) => training._id !== payload
      );
      state.loading = false;
    },
    [deleteTrainingById.rejected]: (state, { payload }) => {
      state.loading = false;
    },
    [getTrainingStudents.pending]: (state) => {
      state.loading = true;
    },
    [getTrainingStudents.fulfilled]: (state, { payload }) => {
      state.training.students = payload;
      state.loading = false;
    },
    [getTrainingStudents.rejected]: (state, { payload }) => {
      state.loading = false;
    },
    [addStudentToTraining.pending]: (state) => {
      state.loading = true;
    },
    [addStudentToTraining.fulfilled]: (state, { payload }) => {
      console.log(payload);
      if (state.training && state.training.students) {
        state.training.students = payload;
      }
      state.loading = false;
    },
    [addStudentToTraining.rejected]: (state) => {
      state.loading = false;
    },
    [deleteStudentFromTraining.pending]: (state) => {
      state.loading = true;
    },
    [deleteStudentFromTraining.fulfilled]: (state, { payload }) => {
      const { studentFormationId } = payload;
      state.training = {
        ...state.training,
        students: state.training.students.filter(
          (student) => student.studentFormationId !== studentFormationId
        ),
      };
      state.loading = false;
    },
    [getStudentFormationsForFormation.pending]: (state) => {
      state.loading = true;
    },
    [getStudentFormationsForFormation.fulfilled]: (state, { payload }) => {
      state.availableStudentFormations = payload;
      state.loading = false;
    },
    [getStudentFormationsForFormation.rejected]: (state, { payload }) => {
      state.error = payload;
      state.loading = false;
      state.availableStudentFormations = [];
    },
    // Gestion de l'approbation d'un instructeur
    [approveInstructor.pending]: (state) => {
      state.loading = true;
    },
    [approveInstructor.fulfilled]: (state, { payload }) => {
      if (state.training && state?.training?._id === payload?.trainingId) {
        state.training.instructors = state.training.instructors.map(
          (instructor) =>
            instructor?.instructorId?._id ===
            payload?.instructor?.instructorId?._id
              ? payload.instructor
              : instructor
        );
      }
      state.loading = false;
    },
    [approveInstructor.rejected]: (state) => {
      state.loading = false;
    },
    //get Students For Training
    [getStudentsForTraining.pending]: (state) => {
      state.loading = true;
    },
    [getStudentsForTraining.fulfilled]: (state, { payload }) => {
      state.studentsForTraining = payload;
      state.loading = false;
    },
    [getStudentsForTraining.rejected]: (state, { payload }) => {
      state.loading = false;
      state.studentsForTraining = [];
      state.error = payload;
    },
    // Gestion du rejet d'un instructeur
    [rejectInstructor.pending]: (state) => {
      state.loading = true;
    },
    [rejectInstructor.fulfilled]: (state, { payload }) => {
      if (state.training && state.training._id === payload.trainingId) {
        state.training.instructors = state.training.instructors.map(
          (instructor) =>
            instructor._id === payload.instructorId
              ? { ...instructor, isApproved: "rejected" }
              : instructor
        );
      }
      state.loading = false;
    },
    [rejectInstructor.rejected]: (state) => {
      state.loading = false;
    },
    [assignInstructorToTraining.pending]: (state) => {
      state.loading = true;
    },
    [assignInstructorToTraining.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.training = payload;
      }
      state.loading = false;
    },
    [assignInstructorToTraining.rejected]: (state) => {
      state.loading = false;
    },
    // Remove discount
    [removeDiscountFromTraining.pending]: (state) => {
      state.loading = true;
    },
    [removeDiscountFromTraining.fulfilled]: (state) => {
      state.loading = false;
    },
    [removeDiscountFromTraining.rejected]: (state, { payload }) => {
      state.error = payload;
      state.loading = false;
    },
  },
});

export const {
  setTrainings,
  setTraining,
  setLoading,
  setError,
  setAvailableStudentFormations,
} = trainingsSlice.actions;

export default trainingsSlice.reducer;
